<template>
  <div class="ma-3 root-container">
    <div class="title">
      Controls
    </div>
    <div
      v-if="loaded"
      id="main-container"
      ref="main-container"
      layout="column"
    >
      <div
        id="control-header"
        class="headline"
      >
        <v-avatar
          :size="32"
          :title="sessionStatusStrLong(session.status)"
          :color="sessionStatusColor(session.status)"
          style="font-size: 16px"
          class="black--text font-weight-medium ml-3"
        >
          {{ sessionStatusStr(session.status) }}
        </v-avatar>
        <v-icon
          color="black"
          class="mx-2"
          small
        >
          <template v-if="session.application === Consts.APPLICATION.BEESECURE">
            icon-bee48
          </template>
          <template v-else>
            icon-kayak48
          </template>
        </v-icon>
        <router-link :to="{name: 'sessions-show-info' , params:{id: session.id}}">
          #{{ session.reference }}
        </router-link>
        <span class="text-uppercase ml-1"> [{{ session.lang_id ? session.lang_id : '?' }}]</span>
        <v-chip
          :color="sessionTypeColor(session.session_type)"
          class="lighten-3 ml-3"
          small
        >
          {{ sessionTypeStr(session.session_type) }}
        </v-chip>
        <v-icon
          v-if="session.package_id"
          class="ml-2"
          color="primary"
          title="In a package"
        >
          mdi-timetable
        </v-icon>
        <span
          v-if="session.topic"
          class="subheading"
        > - {{ session.topic.name }} - </span>
        <span
          v-if="sessionDates()"
          class="subheading"
          style="color: grey"
        > {{ sessionDates() }} </span>

        <span v-if="session.class_name">
          <span class="ml-2 mr-1">/</span>
          <b>{{ session.class_name }}</b>
        </span>

        <template v-if="session.ticket_number">
          <v-icon
            class="ml-3"
            color="primary"
            title="Ticket number"
          >
            mdi-ticket
          </v-icon>{{ session.ticket_number }}
        </template>
        <div class="ml-2">
          <span v-if="session.organization">&nbsp;-&nbsp;{{ session.organization.name }}</span>
          <span
            v-if="session.team"
            class="font-weight-light"
          >
            <span
              v-if="session.organization"
              class="ml-2 mr-1"
            >/</span>
            {{ session.team.name }}
          </span>
        </div>
      </div>
      <div id="back">
        <router-link
          :to="{name: 'payments-controls-index'}"
          class="link-unstyled py-2 no-printable mb-3"
          style="width: max-content"
        >
          <v-icon
            color="primary"
            size="16"
          >
            mdi-arrow-left
          </v-icon>
          Back to the list
        </router-link>
      </div>
      <hr>
      <div id="control-main-container">
        <div
          id="daggesrapport"
          class="control-inner-bloc"
        >
          <h2>Daggesrapport</h2>
          <div v-if="!session.daggesrapport">
            Hmmm..., it seems that there is no Daggesrapport here
          </div>
          <div v-else>
            <div class="inner-bloc-header">
              <div class="figure">
                <span class="number">{{ session.daggesrapport.number_of_participants }}</span>
                <span class="legend">participants</span>
              </div>
              <div
                class="figure"
                :class="{problems: session.daggesrapport.problems.length > 0}"
              >
                <span class="number">{{ session.daggesrapport.problems.length }}</span>
                <span class="legend">problem{{ session.daggesrapport.problems.length > 1 ? 's' : '' }}</span>
              </div>
              <problems
                v-if="session.daggesrapport.problems.length > 0"
                :problems="session.daggesrapport.problems"
                class="problems"
              />
            </div>
            <h3 class="mb-1">
              Report
            </h3>
            <div class="form-ro">
              <div v-if="session.daggesrapport.fields.length > 0">
                <readonly-field
                  v-for="f in session.daggesrapport.fields"
                  :key="f.id"
                  :field="f"
                  class="field"
                />
              </div>
              <em v-else>No field for this form</em>
            </div>
            <div id="validations">
              <h3 class="mb-1">
                Validations
              </h3>
              <div id="validations-wrapper">
                <div
                  v-for="v in session.daggesrapport.validations"
                  :key="v.id"
                  class="validation mb-3"
                >
                  <div class="validation-header">
                    <h4>{{ fullnameOfHumanWithID(v.trainer_id) }}</h4>
                  </div>
                  <div>
                    <div class="comment">
                      <div
                        v-if="v.comment"
                        class="bubble"
                      >
                        <p class="nl2br">
                          {{ v.comment }}
                        </p>
                      </div>
                      <p v-else>
                        (no comment)
                      </p>
                    </div>
                    <em class="validated">validated <span style="width: 100px">{{ formatDayHuman(v.created_at) }}</span> at {{ formatTime(v.created_at) }}</em>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          id="evaluations"
          class="control-inner-bloc"
        >
          <h2>Evaluation{{ evaluations.length > 1 ? 's' :'' }}</h2>
          <div class="inner-bloc-header">
            <div class="humans">
              <div
                v-for="h in humans"
                :key="h.id"
                class="human"
                @click="selectEvaluation(h.evaluation.id)"
              >
                <div
                  class="avatar"
                  :class="{selected: currentHuman.id == h.id}"
                  :title="h.is_intern ? 'Intern' : 'Trainer/AS'"
                >
                  <div
                    v-if="!h.is_intern"
                    class="pastille"
                    :class="{is_controlled: h.evaluation.is_controlled}"
                    :title="h.evaluation.is_controlled ? 'Control already validated' : 'Not yet controlled'"
                  >
&nbsp;
                  </div>
                  <div
                    v-if="hasPersonalDataMissing(h.evaluation)"
                    class="pastille-left"
                    title="This user has personnal data missing"
                  >
                    <v-icon
                      color="#F54927"
                      size="20px"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </div>
                  <v-icon v-if="! h.is_intern">
                    mdi-account
                  </v-icon>
                  <v-icon v-else>
                    mdi-account-star
                  </v-icon>
                </div>
                <div class="name">
                  {{ h.firstname }} {{ h.lastname }}
                </div>
              </div>
            </div>
          </div>
          <transition
            name="slide"
            mode="out-in"
          >
            <div
              v-if="displayedEval"
              :key="displayedEval.id"
              class="evaluation-container"
            >
              <div class="evaluation">
                <h3 class="mb-1">
                  Report
                </h3>
                <div
                  v-if="displayedEval.fields.length"
                  class=" form-ro"
                >
                  <h3 v-if="currentHuman.is_intern">
                    <em>Intern followed by <strong>{{ nameOfExpertFollowing(currentHuman) }}</strong></em>
                  </h3>
                  <readonly-field
                    v-for="f in displayedEval.fields"
                    :key="f.id"
                    :field="f"
                    class="field"
                  />
                </div>
                <div v-else>
                  Hmmm..., it seems that there is no form here
                </div>
              </div>
              <div
                v-if="!currentHuman.is_intern"
                class="meta-data"
              >
                <div class="number-of-hours">
                  <h3>Total working time</h3>
                  <div class="working-time">
                    <div
                      class="wt"
                      :class="{overlapped: displayedEval.working_time != session.times[currentHuman.id]}"
                    >
                      {{ (''+displayedEval.working_time).replace('.', ',') }} h
                    </div>
                    <div class="wt-legend">
                      The total time estimated of this session is <strong>{{ (''+session.times[currentHuman.id]).replace('.', ',') }}</strong> h
                    </div>
                  </div>
                  <div
                    v-if="displayedEval.working_time != session.times[currentHuman.id]"
                    class="reason text-grey nl2br"
                  >
                    <strong>Reason given by the trainer/AS : </strong>"{{ displayedEval.working_time_exceeding_reason }}"
                  </div>
                </div>
                <div class="mileages">
                  <h3>Mileage expenses</h3>
                  <div
                    v-if="!displayedEval.has_kms"
                    class="mt-1 text-grey"
                  >
                    <em>No mileage expenses have been declared by the trainer/AS</em>
                  </div>
                  <div v-else>
                    <div class="total-kms">
                      {{ (''+displayedEval.kms).replace('.', ',') }} km
                    </div>
                    <div
                      v-if="displayedEval.has_foreign_kms"
                      class="text-center"
                    >
                      <a
                        v-if="displayedEval.kms_receipt_id"
                        class="underlined"
                        @click="downloadReceipt(displayedEval)"
                      >Download receipt</a>
                    </div>
                    <div
                      v-else
                      class="mt-3"
                    >
                      <div
                        v-for="(s, idx) in displayedEval.steps"
                        :key="idx"
                        class="flex-v-center mb-2 step"
                      >
                        <v-icon>mdi-sign-direction</v-icon>
                        <span>{{ idx+1 }} -&nbsp;</span>
                        <span>{{ cities.find(c => c.id == s.city_id).name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="!currentHuman.is_intern"
                id="control"
              >
                <h3>Control</h3>
                <v-switch
                  v-model="corrections[currentEvalID]"
                  :readonly="alreadyPublished || hasPersonalDataMissing(displayedEval)"
                  label="This evaluation needs corrections"
                  class="switch"
                  color="primary"
                  @change="onCorrectionChanged(displayedEval)"
                />
                <div class="control-form">
                  <v-form
                    ref="form"
                  >
                    <div v-if="corrections[currentEvalID]">
                      <v-text-field
                        v-model="displayedEval.controlled_working_time"
                        :readonly="alreadyPublished || hasPersonalDataMissing(displayedEval)"
                        label="Corrected working time"
                        outline
                        :rules="[v => !!v || 'This field is required']"
                      />
                      <v-text-field
                        v-model="displayedEval.controlled_kms"
                        :readonly="alreadyPublished || hasPersonalDataMissing(displayedEval)"
                        label="Corrected kilometers"
                        outline
                        :rules="[v => !!v || 'This field is required']"
                      />
                    </div>

                    <v-textarea
                      v-model="displayedEval.control_comment"
                      :readonly="alreadyPublished || hasPersonalDataMissing(displayedEval)"
                      placeholder="Leave a comment ..."
                      :rules="[v => !!v || !corrections[currentEvalID] || 'This field is required' ]"
                    />
                    <v-btn
                      depressed
                      color="primary"
                      :disabled="alreadyPublished || hasPersonalDataMissing(displayedEval) || saving"
                      @click="saveControl()"
                    >
                      Validate the control
                    </v-btn>
                  </v-form>
                  <div
                    v-if="displayedEval.is_controlled"
                    :key="displayedEval.controlled_at"
                  >
                    <em>Last validation by {{ displayedEval.controller.firstname }} {{ displayedEval.controller.lastname }}, {{ formatDayHuman(displayedEval.controlled_at) }} at {{ formatTime(displayedEval.controlled_at) }} </em>
                  </div>
                  <div
                    v-if="alreadyPublished"
                    class="legend mt-2"
                  >
                    <em>The indemnities for this trainer/AS and this session have already been published</em>
                  </div>
                  <div
                    v-if="hasPersonalDataMissing(displayedEval)"
                    class="flex-v-center"
                  >
                    <v-icon
                      color="#F54927"
                      size="24px"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <span class="ml-2">The user has personnal data missing, you can't proceed to the control of its evaluation</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <em>No evaluation found</em>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Sessions from '@/api/sessions'
  import SessionsMixin from '@/mixins/sessions'
  import DateMixin from '@/mixins/date'
  import Consts from '@/consts'
  import moment from 'moment'
  import axios from 'axios'
  import Vue from 'vue'
  import ReadonlyField from '@/components/sessions/evals/GenericForm/ReadonlyField'
  import Problems from '@/components/payments/controls/Problems'
  import Params from '@/api/params'

  export default {
    components: {ReadonlyField, Problems},
    mixins: [SessionsMixin, DateMixin],
    data() {
      return {
        loaded: false,
        session: null,
        Consts,
        currentEvalID: null,
        cities: [],
        corrections: {},
        saving: false,
        alreadyPublished: false
      }
    },
    computed: {
      evaluations() {
        return [...this.session.trainers_evaluations, ...this.session.interns_evaluations]
      },
      humans() {
        return this.evaluations.reduce((acc, e) => {
          const listToUse = e.form_type === Consts.EVALUATIONS_TYPES.EVALUATION_INTERN ? this.session.visitors : this.session.trainers
          const t = listToUse.find(ltu => ltu.id == e.user_id)
          if(t) {
            acc.push({
              id: t.id,
              firstname: t.firstname,
              lastname: t.lastname,
              matricule: t.matricule,
              streetnumber: t.streetnumber,
              streetname: t.streetname,
              postalcode: t.postalcode,
              city: t.city,
              iban: t.iban,
              bic: t.bic,
              is_intern: e.form_type === Consts.EVALUATIONS_TYPES.EVALUATION_INTERN,
              evaluation: e
            })
            return acc
          }
        }, [])
      },
      displayedEval() {
        return this.currentEvalID ? this.humans.find(h => h.evaluation.id == this.currentEvalID).evaluation : null
      },
      currentHuman() {
        return this.currentEvalID ? this.humans.find(h => h.evaluation.id == this.currentEvalID) : null
      }
    },
    async mounted() {
      //on doit charger la session avec ses évaluations et leurs sous-éléments
      const {data} = await Sessions.getDataToControl(this.$route.params.id)
      this.session = data
      const {data: cities} = await Params.getList('cities', {is_from_geoportail: 1})
      this.cities = cities

      this.loaded = true
      this.selectEvaluation(this.evaluations.length ? this.evaluations[0].id : null)

      this.session.trainers_evaluations.forEach(e => {
        this.formatFloats(e)
        Vue.set(this.corrections, e.id, e.kms != e.controlled_kms || e.working_time != e.controlled_working_time)
      })

      //pour permettre la comparaison et dire si on dépasse ou pas
      Object.keys(this.session.times).forEach(k => {
        this.session.times[k] = (''+parseFloat(this.session.times[k] ?? 0)).replace('.', ',')
      })
    },
    methods: {
      formatFloats(e) {
        e.kms = (''+parseFloat(e.kms ?? 0)).replace('.', ',')
        e.controlled_kms = (''+parseFloat(e.controlled_kms ?? 0)).replace('.', ',')
        e.working_time = (''+parseFloat(e.working_time ?? 0)).replace('.', ',')
        e.controlled_working_time = (''+parseFloat(e.controlled_working_time ?? 0)).replace('.', ',')
      },
      formatDate(x) {
        return moment(x).format("DD.MM.YYYY");
      },
      formatTime(x) {
        return moment(x).format("HH:mm");
      },
      getDates(start, end) {
        if (this.formatDate(start) == this.formatDate(end)) {
          return (
            this.formatDate(start) +
            ", " +
            this.formatTime(start) +
            "-" +
            this.formatTime(end)
          );
        } else {
          return (
            this.formatDate(start) +
            ", " +
            this.formatTime(start) +
            " - " +
            this.formatDate(end) +
            ", " +
            this.formatTime(end)
          );
        }
      },
      sessionDates() {
        if (this.session && this.session.dates && this.session.dates.length > 0) {
          return this.getDates(
            this.session.dates[0].start,
            this.session.dates[this.session.dates.length - 1].end
          );
        }
        return false;
      },
      downloadReceipt (e) {
        let a = document.createElement("a");
        a.style = "display: none";
        document.body.appendChild(a);

        axios.get(`/sessions/${this.$route.params.id}/trainers-evaluations/${e.id}/receipt`, {responseType: 'blob'}).then(res => {
          let blob = new Blob([res.data], {type: "octet/stream"});

          if (window.navigator && window.navigator.msSaveOrOpenBlob){
            window.navigator.msSaveBlob(blob, e.receipt.name);
          }
          else
          {
            let url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = e.receipt.name
            a.click();
            window.URL.revokeObjectURL(url);
          }
        })
      },
      onCorrectionChanged(e) {
        //dans tous les cas si on change on revient à la valeur du trainer
        e.controlled_working_time = e.working_time
        e.controlled_kms = e.kms ?? 0
      },
      async canControlIt() {
        if(this.displayedEval.form_type != Consts.EVALUATIONS_TYPES.EVALUATION_INTERN) {
          const {data} = await axios.get(`/sessions/${this.$route.params.id}/trainers-evaluations/${this.displayedEval.id}/indemnities-closed`)
          this.alreadyPublished = data
        }
        else {
          this.alreadyPublished = true
        }
      },
      async saveControl() {
        if (!this.$refs.form.validate()) {
          this.$snotify.error('Please check the content of your form')
          return;
        }
        if(this.saving){
          return
        }

        this.saving = true

        const {data} = await axios.put(`/sessions/${this.$route.params.id}/trainers-evaluations/${this.displayedEval.id}/control`, {
            controlled_working_time: this.displayedEval.controlled_working_time,
            controlled_kms: this.displayedEval.controlled_kms,
            control_comment: this.displayedEval.control_comment
          }
        )

        const savedEvaluation = data.evaluation
        this.formatFloats(savedEvaluation)
        this.saving = false

        this.session.trainers_evaluations.splice(
          this.session.trainers_evaluations.indexOf(this.displayedEval),
          1,
          savedEvaluation
          )

        if(this.session.status == Consts.TOCONTROL && data.closed) { //la session a été closed grâce à ce contrôle
          this.session.status = Consts.CLOSED
        }
        this.$snotify.success('The evaluation has been controlled successfully')
      },
      nameOfExpertFollowing(intern) {
        const sv = this.session.session_visitor.find(sv => sv.user_id == intern.id)
        if(! sv) {
          return '???'
        }
        return this.fullnameOfHumanWithID(sv.trainer_id)
      },
      fullnameOfHumanWithID(id) {
        const human = this.humans.find(h => h.id == id)
        if( ! human) {
          return '???'
        }
        else {
          return `${human.firstname} ${human.lastname}`
        }
      },
      selectEvaluation(id) {
        this.currentEvalID = id
        this.canControlIt() //pour savoir si on a déjà publié les indemnités liées
      },
      hasPersonalDataMissing(evaluation) {
        const h = this.humans.find(h => h.evaluation.id == evaluation.id)
        return !h || !h.matricule || h.matricule.trim() == '' || !h.streetnumber || h.streetnumber.trim() == '' || !h.streetname || h.streetname.trim() == '' || !h.postalcode || h.postalcode.trim() == '' || !h.city || h.city.trim() == '' || !h.iban || h.iban.trim() == '' || !h.bic || h.bic.trim() == ''
      }
    }
  }
</script>

<style scoped lang="sass">
  .root-container
    flex-grow: 1
    align-items: stretch
    overflow: hidden
    display: flex
    flex-direction: column

  #main-container
    display: flex
    flex-direction: column
    flex-grow: 1

  #control-header
    margin-top: 10px
    margin-bottom: 10px
    display: flex
    flex-direction: row
    align-items: center
    flex-wrap: wrap

  #control-main-container
    flex-grow: 1
    display: flex
    flex-direction: row
    flex-wrap: wrap
    padding: 20px 0 20px 0
    align-items: stretch

    .control-inner-bloc
      background-color: white
      margin-left: 10px
      margin-right: 10px
      overflow-y: auto
      padding: 10px

    #daggesrapport
      min-width: 350px
      flex: 1 1 25% /* 5% perdus dans les marges et autres paddings */

      .problems
        max-width: 450px

    #evaluations
      flex: 1 1 70%
      min-width: 350px

    .field
      padding: 5px 0 5px 0
      &:nth-child(odd)
        background-color: #fef9eb

    .form-ro
      background: white
      padding: 1rem 2rem 2rem 2rem
      border-radius: 4px
      box-shadow: 0 10px 15px -5px rgba(0, 0, 0, 0.2)
      margin-bottom: 20px

    .inner-bloc-header
      padding: 10px
      margin-bottom: 10px
      border-bottom: 1px solid #F1F1F1
      display: flex
      flex-direction: row
      justify-content: flex-start
      align-items: flex-start

      .figure
        display: flex
        flex-direction: column
        align-items: center
        margin-right: 30px

        &.problems > .number
          color: tomato

      .number
        font-size: 3rem
        font-weight: bold
        color: #f4b944
  .humans
    display: flex
    align-items: center
    flex-wrap: wrap

  .human
    display: flex
    flex-direction: column
    align-items: center
    margin-right: 20px
    cursor: pointer



    .avatar
      position: relative
      padding: 10px
      display: flex
      align-items: center
      justify-content: center
      width: 60px
      height: 60px
      flex-shrink: 0
      border-radius: 50%
      background-color: #F1F1F1
      border: 3px solid #E1E1E1

      .pastille
        position: absolute
        top: -8px
        right: -8px
        background-color: #FFD569
        border-radius: 8px
        width: 16px
        height: 16px

        &.is_controlled
          background-color: #8DDE5B

      &.selected
        border: 3px solid #f4b944
        background-color: #fef9eb

      .pastille-left
        position: absolute
        top: -8px
        left: -12px
        width: 16px
        height: 16px

    &:hover
      .avatar
        background-color: #fef9eb

  .slide
    &-enter-active
      transition: all 0.05s ease

    &-leave-active
      transition: all 0.3s ease

    &-enter-from
      transform: translateX(100%)
      opacity: 0.5

    &-enter-to
      transform: translateX(0)
      opacity: 1

    &-leave-from
      transform: translateX(0)
      opacity: 1

    &-leave-to
      transform: translateX(-100%)
      opacity: 0

  .evaluation-container
    display: flex
    flex-direction: row
    justify-content: space-around
    align-items: flex-start
    flex-wrap: wrap

    .evaluation
      min-width: 400px
      flex: 1 1 50%

    .meta-data
      padding-left: 20px
      padding-right: 20px
      display: flex
      flex-direction: column
      align-items: flex-start
      min-width: 300px
      flex: 1 1 25%

    #control
      border-left: 1px solid #F1F1F1
      align-self: stretch
      padding-left: 20px
      min-width: 300px
      flex: 1 1 25%

      h2
        margin: 0
        padding: 0

  .number-of-hours
    margin-bottom: 30px
    width: 100%

  .working-time
    margin-top: 10px
    display: flex
    flex-direction: column
    align-items: center

    .wt
      font-weight: bold
      font-size: 2em

      &.overlapped
        color: tomato

    .wt-legend
      color: #A1A1A1

  .reason
    margin-top: 10px

  .mileages
    margin-top: 10px
    width: 100%

  .total-kms
    margin-top: 10px
    text-align: center
    font-weight: bold
    font-size: 2em

  .flex-v-center
    display: flex
    flex-direction: row
    align-items: center

  .flex-center
    display: flex
    flex-direction: row
    justify-content: center

  .text-grey
    color: #A1A1A1

  .step:nth-child(odd)
    background-color: #F5F5F5

  .text-center
    text-align: center

  #validations-wrapper
    p
      margin-bottom: 0px
    .validation
      border-left: 5px solid #F1F1F1
      padding-left: 5px

    .comment
      margin: 5px 0 4px 0

    .validated
      font-size: 0.9em

    .bubble
      border-radius: 7px
      background-color: #fce48a
      display: inline-block
      padding: 15px 18px
      position: relative
      width: 100%

      &:before
        content: "\00a0"
        display: block
        background-color: #fce48a
        height: 20px
        width: 20px
        position: absolute
        top: -7px
        left: 12px
        -webkit-transform: rotate(315deg) skew(5deg)

      > p
        margin: 0
        padding: 0
</style>
